import { Button } from "@monbits/ui";
import { Check, Edit2, Trash2 } from "lucide-react";
import * as React from "react";
import { useAsyncFn } from "react-use";
import { toast } from "sonner";
import CustomCard from "src/components/custom-card/custom-card";
import DrawerForm from "src/components/drawer-form/drawer-form";
import { Loader } from "src/components/loader/loader";
import MerchantForm from "src/components/merchant-form/merchant-form";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import useSharedMerchants from "src/hooks/use-merchants";
import merchantProvider from "src/providers/merchant-provider";
import type { Merchant, MerchantBase } from "src/types";
import { sleep } from "src/utils";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";

const ManageMerchantsPage: React.FC = () => {
	const { merchants, getMerchants } = useSharedMerchants();
	const { merchantId, setMerchantId } = useActiveMerchantId();
	const [activeMerchant, setActiveMerchant] = React.useState<
		Merchant | undefined
	>();
	const [merchantToDelete, setMerchantToDelete] = React.useState<
		Merchant | undefined
	>();
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
		React.useState<boolean>(false);
	const [isAddMerchantFormOpened, setIsAddMerchantFormOpened] =
		React.useState<boolean>(false);
	const [isEditAddMerchantFormOpened, setIsEditAddMerchantFormOpened] =
		React.useState<boolean>(false);
	const [createMerchantReqState, createMerchantReq] = useAsyncFn(
		merchantProvider.storeMerchant,
	);
	const [editMerchantReqState, editMerchantReq] = useAsyncFn(
		merchantProvider.updateMerchant,
	);
	const [merchantLimits, fetchMerchantLimits] = useAsyncFn(
		merchantProvider.getMerchantLimits,
	);

	React.useEffect(() => {
		fetchMerchantLimits();
	}, [fetchMerchantLimits]);
	const [isFormSubmitting, setIsFormSubmitting] =
		React.useState<boolean>(false);

	const resetValues = async () => {
		await sleep(1000);
		setActiveMerchant(undefined);
		setIsAddMerchantFormOpened(false);
		setIsEditAddMerchantFormOpened(false);
		await getMerchants();
	};
	const [resetValuesFnState, resetValuesFn] = useAsyncFn(resetValues);

	const openNotification = (title: string, description: string) => {
		toast.success(title, {
			description,
			icon: <Check className="h-4 w-4" />,
		});
	};

	const uploadCreatedMerchant = async (
		value: MerchantBase,
		reset: () => void,
	) => {
		try {
			setIsFormSubmitting(true);
			const merchant = await createMerchantReq(value);
			resetValuesFn();
			setMerchantId(merchant.id);
			openNotification("Welcome to the Monbits Pay", `Hello, ${value.name}!`);
			fetchMerchantLimits();

			await sleep(1000);
			reset();
		} catch (error) {
			console.error("error occurred: ", error);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const uploadEditedMerchant = async (
		merchantId: string,
		value: MerchantBase,
		reset: () => void,
	) => {
		try {
			setIsFormSubmitting(true);
			await editMerchantReq(merchantId, value);
			resetValuesFn();
			openNotification(
				"Information saved",
				`You have changed information about ${value.name} merchant`,
			);

			await sleep(1000);
			reset();
		} catch (error) {
			console.error("error occurred: ", error);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const onConfirmDeleteMerchant = async (merchant: Merchant) => {
		try {
			setIsFormSubmitting(true);
			await merchantProvider.deleteMerchant(merchant.id);
			await getMerchants();

			const nextMerchant = merchants?.find(
				(merchantItem) => merchantItem.id !== merchant.id,
			);
			setMerchantId(nextMerchant ? nextMerchant.id : null);

			openNotification(
				`Merchant ${merchant.name} has been deleted`,
				"Thank you for being with us",
			);
		} catch (error) {
			console.error("error occurred: ", error);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const onEdit = async (index: number) => {
		setIsEditAddMerchantFormOpened(true);
		setActiveMerchant(merchants?.[index]);
	};

	const isAnyFormOpen = isAddMerchantFormOpened || isEditAddMerchantFormOpened;
	const isTableLoading =
		createMerchantReqState.loading ||
		editMerchantReqState.loading ||
		resetValuesFnState.loading ||
		!merchants;

	const limits = merchantLimits?.value;
	const canCreateMerchant =
		!limits || limits.isUnlimited || limits.currentCount < limits.maxAllowed;

	return (
		<div className="space-y-6 p-4 sm:p-6">
			<h1 className="sr-only">Manage Merchants</h1>
			<div className="relative">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
					<div className="flex items-center gap-3">
						<span aria-hidden className="h-5 w-[3px] rounded-full bg-primary" />
						<p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
							Manage Merchants · Workspaces with separate payments and settings
						</p>
					</div>
					<Button
						onClick={() => setIsAddMerchantFormOpened(true)}
						disabled={!canCreateMerchant}
					>
						Create Merchant
						{limits && !limits.isUnlimited && (
							<span className="ml-2 text-xs opacity-70">
								({limits.currentCount}/{limits.maxAllowed})
							</span>
						)}
					</Button>
				</div>

				{isTableLoading ? (
					<Loader size="lg" fullScreen />
				) : merchants?.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-16 px-8 text-center border rounded-lg bg-muted/50">
						<div className="space-y-4 max-w-md">
							<div className="space-y-2">
								<h3 className="text-lg font-semibold">No merchants yet</h3>
								<p className="text-sm text-muted-foreground">
									Create your first merchant to start accepting crypto payments
								</p>
							</div>
						</div>
					</div>
				) : (
					<div className="space-y-3">
						{merchants?.map((merchant, index) => (
							<CustomCard
								key={merchant.id}
								title={merchant.name}
								description={merchant.website}
								rightIcon={
									<div className="flex gap-2">
										<Button
											disabled={isAnyFormOpen}
											variant="ghost"
											size="icon"
											aria-label="Edit merchant"
											onClick={() => onEdit(index)}
										>
											<Edit2 className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											aria-label="Delete merchant"
											disabled={isAnyFormOpen}
											className="text-destructive hover:text-destructive"
											onClick={() => {
												setMerchantToDelete(merchant);
												setIsDeleteDialogOpen(true);
											}}
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
								}
								isActive={merchant.id === merchantId}
							/>
						))}
					</div>
				)}

				{activeMerchant && (
					<DrawerForm
						title="Edit merchant"
						description="Update merchant information and settings."
						isOpen={isEditAddMerchantFormOpened}
						onClose={() => setIsEditAddMerchantFormOpened(false)}
						onSubmit={() => {
							const form = document.querySelector("form");
							if (form) {
								form.dispatchEvent(
									new Event("submit", { cancelable: true, bubbles: true }),
								);
							}
						}}
						isSubmitting={isFormSubmitting}
						submitText="Save Changes"
						width={512}
					>
						<MerchantForm
							isFormSubmitting={isFormSubmitting}
							activeMerchant={activeMerchant}
							onFinish={(values: MerchantBase, reset: () => void) =>
								uploadEditedMerchant(activeMerchant.id, values, reset)
							}
						/>
					</DrawerForm>
				)}
				<DrawerForm
					title="Create merchant"
					description="Create a new merchant workspace to accept payments."
					isOpen={isAddMerchantFormOpened}
					onClose={() => setIsAddMerchantFormOpened(false)}
					onSubmit={() => {
						const form = document.querySelector("form");
						if (form) {
							form.dispatchEvent(
								new Event("submit", { cancelable: true, bubbles: true }),
							);
						}
					}}
					isSubmitting={isFormSubmitting}
					submitText="Create Merchant"
					width={512}
				>
					<MerchantForm
						isFormSubmitting={isFormSubmitting}
						onFinish={uploadCreatedMerchant}
					/>
				</DrawerForm>

				<DeleteConfirmationDialog
					open={isDeleteDialogOpen}
					onOpenChange={setIsDeleteDialogOpen}
					onConfirm={() => {
						if (merchantToDelete) {
							return onConfirmDeleteMerchant(merchantToDelete);
						}
					}}
					title="Delete merchant"
					description="This action cannot be undone. This will permanently delete the merchant and all associated data."
					itemName={merchantToDelete?.name}
					confirmText="delete"
				/>
			</div>
		</div>
	);
};

export default ManageMerchantsPage;
